/**
 * Asset Activities Step (wizard mode)
 *
 * The asset wizard's combined entry + review screen. Renders one
 * collapsible block per `StagedAsset` — manual path produces 1, bulk
 * XLSX path produces N. Each block shows the asset summary header, a
 * `StagingTable` of its activities (with factor-preview, kebab edit /
 * delete / re-run search, and the inline factor picker), and a per-block
 * "+ Add Activity" toolbar action that opens the picker → form modal.
 *
 * Submit fires from this step's bottom-bar CTA (handled in
 * `ActivityFormFlow`) — there is no separate review step. The post-submit
 * `result` step reads from the API response, not from formData.
 *
 * Admin mode (adding activities to an already-created asset on the
 * detail page) lives in `AddActivityToExistingAssetButton.tsx`. The two
 * paths share the picker categories + `createEmptyActivity` helper
 * exported from this file.
 */

import { useCallback, useState } from 'react';
import { FileText, Plus, Fuel, type LucideIcon } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import {
  AssetType,
  EmissionScope,
  Scope1ActivityType,
  Scope2ActivityType,
  type ActivityType,
  ActivityUnit,
  FuelType,
  VehicleType,
  RefrigerantGasType,
  FugitiveGasType,
  ProcessType,
  type AlternativeFactor,
} from '../../types/emissions';
import {
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
  type AssetActivityEntry,
  type StagedRow,
  type StagedAsset,
  type BulkGroup,
  createStagedRow,
  updateStagedAssetActivities,
} from './types';
import { AssetSummaryCard } from './AssetSummaryCard';
import { StagingTable } from './StagingTable';
import { AddActivityModal } from './multi-add/AddActivityModal';
import { ScanInvoiceModal, type ScanInvoiceAccept } from './multi-add/ScanInvoiceModal';
import { useFactorPreview } from '../../hooks/useFactorPreview';
import { type UseUploadSessionResult } from '../../hooks/useUploadSession';

// StagingTable's `groupById` map is only consulted when a row carries a
// `bulkGroupId` — wizard-mode rows are all manual entry today, so an
// empty map is safe. Hoisted to module scope for stable reference.
const EMPTY_GROUP_MAP: Map<string, BulkGroup> = new Map();

// ─── Suggested activity definitions ────────────────────────────────────────

export interface SuggestedActivity {
  scope: EmissionScope;
  activityType: ActivityType;
  label: string;
  description: string;
  /** Pre-set values applied when creating this activity (shown read-only in modal) */
  defaults?: Partial<Pick<AssetActivityEntry, 'vehicleType' | 'fuelType' | 'refrigerantType' | 'processType'>>;
  /** Override icon instead of deriving from activityType */
  Icon?: LucideIcon;
  iconBg?: string;
  iconColor?: string;
}

export interface ActivityCategory {
  label: string;
  description: string;
  activities: SuggestedActivity[];
}

const FACILITY_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Purchased Energy',
    description: 'Energy purchased from the grid or external networks',
    activities: [
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedElectricity, label: 'Electricity', description: 'Grid electricity for lighting, equipment, and HVAC' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedHeating, label: 'Heating', description: 'Heat purchased from an external district heating network' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedCooling, label: 'Cooling', description: 'Cooling purchased from an external district cooling network' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedSteam, label: 'Steam', description: 'Steam purchased from a third-party provider' },
    ],
  },
  {
    label: 'Building Systems',
    description: 'On-site systems that burn fuel or emit greenhouse gases',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Heating & Boilers', description: 'Natural gas, oil, or other fuel burned in boilers, furnaces, or heaters (Stationary Combustion)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Backup Generators', description: 'Diesel or gas generators for emergency or peak power (Stationary Combustion)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.FugitiveEmissions, label: 'HVAC & Refrigeration', description: 'Refrigerant leakage from air conditioning, chillers, and cooling systems (Fugitive Emissions)' },
    ],
  },
];

const MANUFACTURING_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Purchased Energy',
    description: 'Energy purchased from the grid or external networks',
    activities: [
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedElectricity, label: 'Electricity', description: 'Grid electricity for production lines, machinery, and lighting' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedHeating, label: 'Heating', description: 'Heat purchased from an external district heating network' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedCooling, label: 'Cooling', description: 'Cooling purchased from an external district cooling network' },
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedSteam, label: 'Steam', description: 'Steam purchased from a third-party provider for process use' },
    ],
  },
  {
    label: 'On-Site Combustion',
    description: 'Fuel burned on-site in process equipment and generators',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Furnaces, Kilns & Boilers', description: 'Fuel burned in process heating equipment (Stationary Combustion)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Backup Generators', description: 'Diesel or gas generators for emergency or peak power (Stationary Combustion)' },
    ],
  },
  {
    label: 'Building Systems',
    description: 'HVAC and comfort cooling systems',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.FugitiveEmissions, label: 'HVAC & Refrigeration', description: 'Refrigerant leakage from air conditioning and comfort cooling systems (Fugitive Emissions)' },
    ],
  },
  {
    label: 'Industrial Processes',
    description: 'Emissions from chemical transformations and process gas leakage',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.ProcessEmissions, label: 'Cement, Steel, Glass & Lime', description: 'CO₂ released from chemical transformations in production (Process Emissions)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.FugitiveEmissions, label: 'Industrial Refrigerants & Gas', description: 'Leakage from process cooling, sealed systems, and industrial piping (Fugitive Emissions)' },
    ],
  },
];

const VEHICLE_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Fuel Consumption',
    description: 'Fuel used by this vehicle',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.MobileCombustion, label: 'Mobile Fuel', description: 'Fuel consumed by this vehicle (petrol, diesel, electric, etc.)', Icon: Fuel, iconBg: 'tw:bg-orange-100', iconColor: 'tw:text-orange-600' },
    ],
  },
];

const DATA_CENTRE_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Purchased Energy',
    description: 'Electricity purchased from the grid',
    activities: [
      { scope: EmissionScope.Scope2, activityType: Scope2ActivityType.PurchasedElectricity, label: 'IT & Cooling Electricity', description: 'Grid electricity powering servers, storage, networking, and CRAC/CRAH cooling' },
    ],
  },
  {
    label: 'On-Site Systems',
    description: 'Generators, chillers, and cooling infrastructure',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Backup Generators', description: 'Diesel or gas generators for UPS and emergency power (Stationary Combustion)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.FugitiveEmissions, label: 'Chillers & CRAC Units', description: 'Refrigerant leakage from chillers, CRAC/CRAH units, and in-row cooling (Fugitive Emissions)' },
    ],
  },
];

const EQUIPMENT_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Equipment Emissions',
    description: 'Fuel consumption and gas leakage from equipment',
    activities: [
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.StationaryCombustion, label: 'Fuel Consumption', description: 'Diesel, gas, or other fuel burned by the equipment during operation (Stationary Combustion)' },
      { scope: EmissionScope.Scope1, activityType: Scope1ActivityType.FugitiveEmissions, label: 'Gas Leakage', description: 'Industrial gas or refrigerant leakage from sealed systems (Fugitive Emissions)' },
    ],
  },
];

export const OTHER_CATEGORIES: ActivityCategory[] = [
  {
    label: 'Scope 1 — Direct Emissions',
    description: 'Emissions from sources owned or controlled by your organisation',
    activities: SCOPE1_ACTIVITY_TYPES.map((a) => ({
      scope: EmissionScope.Scope1, activityType: a.value, label: a.label, description: a.description,
    })),
  },
  {
    label: 'Scope 2 — Purchased Energy',
    description: 'Indirect emissions from purchased electricity, heating, cooling, or steam',
    activities: SCOPE2_ACTIVITY_TYPES.map((a) => ({
      scope: EmissionScope.Scope2, activityType: a.value, label: a.label, description: a.description,
    })),
  },
];

export const ASSET_CATEGORIES: Record<AssetType, ActivityCategory[]> = {
  [AssetType.Facility]: FACILITY_CATEGORIES,
  [AssetType.ManufacturingFacility]: MANUFACTURING_CATEGORIES,
  [AssetType.Vehicle]: VEHICLE_CATEGORIES,
  [AssetType.DataCentre]: DATA_CENTRE_CATEGORIES,
  [AssetType.Equipment]: EQUIPMENT_CATEGORIES,
  [AssetType.Other]: OTHER_CATEGORIES,
};

// ─── Helpers (also consumed by AddActivityToExistingAssetButton) ──────────

export interface NewActivityParams {
  sourceLabel: string;
  scope: EmissionScope;
  activityType: ActivityType;
  region: string;
  periodStart: string;
  periodEnd: string;
  name: string;
}

export const createEmptyActivity = (params: NewActivityParams): AssetActivityEntry => ({
  ...params,
  quantity: '',
  unit: '' as ActivityUnit,
  fuelType: '' as FuelType,
  vehicleType: '' as VehicleType,
  refrigerantType: '' as RefrigerantGasType | FugitiveGasType,
  processType: '' as ProcessType,
  regionFallback: true,
  yearFallback: true,
  dataSource: '',
  notes: '',
});

// ─── Wizard component ──────────────────────────────────────────────────────

/**
 * Functional-updater form of the parent's stagedAssets setter. Mirrors
 * `React.Dispatch<SetStateAction<...>>` — the wizard's async handlers
 * (factor preview lands per-row, possibly racing across N blocks) need
 * to read fresh `stagedAssets` rather than a snapshot captured at
 * render time. Closure-captured arrays were silently dropping updates.
 */
type StagedAssetsUpdater = (next: (prev: StagedAsset[]) => StagedAsset[]) => void;

interface AssetActivitiesStepProps {
  stagedAssets: StagedAsset[];
  onStagedAssetsChange: StagedAssetsUpdater;
  /**
   * Initiative id — needed by the factor-search hook that populates
   * each row's resolved factor + alternatives.
   */
  initiativeId: string;
  /**
   * Org-level default region (`EmissionSettings.defaultRegion`) — used
   * to pre-populate the Factor Region on new activity rows when the
   * staged asset itself has no region set. The asset's region wins
   * if it's there (manual path with country picked, or bulk row with
   * a region cell), so this is just the fallback.
   */
  defaultRegion?: string;
  /**
   * Shared upload session (GU-6981). When provided, each asset block
   * renders an "Upload Invoice" button alongside "Add Activity" — OCR'd
   * rows land in that specific asset's activities. The post-submit
   * Evidence step reads the same session so the invoices are
   * pre-attachable as evidence on the activities they produced.
   * Optional so tests / legacy callers without the session can omit it.
   */
  uploadSession?: UseUploadSessionResult;
}

/**
 * One collapsible block. Owns its own open/closed state + per-block
 * modal state — multiple blocks on screen simultaneously, each can have
 * its own modal open without colliding.
 */
const AssetBlock = ({
  staged,
  index,
  onStagedAssetsChange,
  initiativeId,
  defaultOpen,
  defaultRegion,
  uploadSession,
}: {
  staged: StagedAsset;
  index: number;
  onStagedAssetsChange: StagedAssetsUpdater;
  initiativeId: string;
  defaultOpen: boolean;
  defaultRegion: string;
  uploadSession?: UseUploadSessionResult;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    editIndex: number | null;
    activity: AssetActivityEntry | null;
    label: string;
    lockedFields: Set<string>;
  }>({ isOpen: false, editIndex: null, activity: null, label: '', lockedFields: new Set() });
  const [scanInvoiceModalOpen, setScanInvoiceModalOpen] = useState(false);

  const assetType = staged.asset.assetType;
  const categories = ASSET_CATEGORIES[assetType] ?? OTHER_CATEGORIES;
  const activities = staged.activities;

  // Factor-preview hook scoped to this block's rows. Each block has its
  // own hook instance; the hook keys searches by row id which is uuid'd
  // across all blocks so there's no collision risk.
  //
  // Both setters route through the parent's functional updater so each
  // async factor-search response reads fresh state — multiple in-flight
  // searches across multiple rows would otherwise race and clobber
  // each other's updates.
  const setRowsForHook = (updater: (prev: StagedRow[]) => StagedRow[]) => {
    onStagedAssetsChange((prev) => updateStagedAssetActivities(prev, index, updater));
  };
  const { retrigger } = useFactorPreview(activities, initiativeId, setRowsForHook);

  const setActivities = (next: StagedRow[] | ((prev: StagedRow[]) => StagedRow[])) => {
    onStagedAssetsChange((prev) => updateStagedAssetActivities(prev, index, next));
  };

  // ─── Add / Edit / Delete handlers ───────────────────────────────────────

  const handleAddSave = (entry: AssetActivityEntry) => {
    if (modalState.editIndex !== null) {
      const editIndex = modalState.editIndex;
      setActivities((prev) => prev.map((row, i) => {
        if (i !== editIndex) {
          return row;
        }
        // Edit drops cached factor fields so the preview hook re-fires
        // against the new classification.
        return {
          ...row,
          request: entry,
          resolvedFactorId: undefined,
          resolvedFactorName: undefined,
          resolvedFactorValue: undefined,
          resolvedFactorUnit: undefined,
          factorAlternatives: undefined,
          resolveAttempted: false,
          overrideFactorId: undefined,
          overrideFactorName: undefined,
          overrideFactorValue: undefined,
          overrideFactorUnit: undefined,
        };
      }));
    } else {
      setActivities((prev) => [...prev, createStagedRow(entry)]);
    }
    setModalState({ isOpen: false, editIndex: null, activity: null, label: '', lockedFields: new Set() });
  };

  // GU-6981: rows returned from ScanInvoiceModal are CreateActivityRequest
  // shaped. Map them to AssetActivityEntry (the modal-shape used by manual
  // rows) so they land in the same StagedRow → toCreateRequest path. Tag
  // each row with `sourceLabel: 'Scan invoice'` so the StagingTable shows
  // provenance distinct from manually-added activities.
  const handleScanInvoiceAccept = useCallback(
    (result: ScanInvoiceAccept) => {
      const newRows = result.activities.map((req) => {
        const entry: AssetActivityEntry = {
          sourceLabel: 'Scan invoice',
          scope: req.scope,
          activityType: req.activityType,
          quantity: String(req.quantity),
          unit: req.unit,
          fuelType: req.fuelType ?? '',
          vehicleType: req.vehicleType ?? '',
          refrigerantType: (req.refrigerantType ?? req.gasType ?? '') as AssetActivityEntry['refrigerantType'],
          processType: req.processType ?? '',
          region: req.region,
          regionFallback: req.regionFallback ?? true,
          yearFallback: req.yearFallback ?? true,
          periodStart: req.periodStart,
          periodEnd: req.periodEnd,
          name: req.name ?? '',
          dataSource: req.dataSource ?? '',
          notes: req.notes ?? '',
        };
        return createStagedRow(entry);
      });
      setActivities((prev) => [...prev, ...newRows]);
      setScanInvoiceModalOpen(false);
    },
    [setActivities],
  );

  const findLabelForActivity = (activity: AssetActivityEntry): string => {
    if (activity.sourceLabel) return activity.sourceLabel;
    const brand = [...SCOPE1_ACTIVITY_TYPES, ...SCOPE2_ACTIVITY_TYPES].find((b) => b.value === activity.activityType);
    return brand?.label ?? activity.activityType;
  };

  const handleEditFromTable = (rowId: string) => {
    const idx = activities.findIndex((r) => r.id === rowId);
    if (idx < 0) {
      return;
    }
    const entry = activities[idx].request;
    const locked = new Set<string>();
    for (const cat of categories) {
      const match = cat.activities.find((s) => s.label === entry.sourceLabel);
      if (match?.defaults) {
        for (const key of Object.keys(match.defaults)) {
          locked.add(key);
        }
        break;
      }
    }
    setModalState({ isOpen: true, editIndex: idx, activity: { ...entry }, label: findLabelForActivity(entry), lockedFields: locked });
  };

  const handleDeleteFromTable = (rowId: string) => {
    setActivities((prev) => prev.filter((r) => r.id !== rowId));
  };

  // ─── Factor picker handlers (override / revert / re-run search) ─────────

  const handlePickAlternative = (rowId: string, factor: AlternativeFactor) => {
    setActivities((prev) => prev.map((row) => {
      if (row.id !== rowId) {
        return row;
      }
      // Picking the auto-resolved factor itself is a no-op override.
      if (factor.id === row.resolvedFactorId) {
        return {
          ...row,
          overrideFactorId: undefined,
          overrideFactorName: undefined,
          overrideFactorValue: undefined,
          overrideFactorUnit: undefined,
        };
      }
      return {
        ...row,
        overrideFactorId: factor.id,
        overrideFactorName: factor.name,
        overrideFactorValue: factor.factor ?? undefined,
        overrideFactorUnit: factor.unit,
      };
    }));
  };

  const handleRevertOverride = (rowId: string) => {
    setActivities((prev) => prev.map((row) =>
      row.id === rowId
        ? {
            ...row,
            overrideFactorId: undefined,
            overrideFactorName: undefined,
            overrideFactorValue: undefined,
            overrideFactorUnit: undefined,
          }
        : row,
    ));
  };

  const handleRetriggerFactorSearch = (rowId: string) => {
    retrigger(rowId);
    setActivities((prev) => prev.map((row) =>
      row.id === rowId
        ? {
            ...row,
            resolvedFactorId: undefined,
            resolvedFactorName: undefined,
            resolvedFactorValue: undefined,
            resolvedFactorUnit: undefined,
            factorAlternatives: undefined,
            resolveAttempted: false,
            overrideFactorId: undefined,
            overrideFactorName: undefined,
            overrideFactorValue: undefined,
            overrideFactorUnit: undefined,
          }
        : row,
    ));
  };

  // Vehicle assets pre-set vehicleType on every new activity and lock
  // the field — the asset already commits to a vehicle type.
  const extraEntryDefaults: Partial<AssetActivityEntry> | undefined =
    assetType === AssetType.Vehicle && staged.asset.vehicleType
      ? { vehicleType: staged.asset.vehicleType }
      : undefined;
  const extraLockedFields = extraEntryDefaults?.vehicleType ? ['vehicleType'] : undefined;

  return (
    <Card className='tw:mb-4'>
      <CardContent className='tw:p-0'>
        <div className='tw:px-6 tw:border-t tw:border-border'>
          <AssetSummaryCard staged={staged} onClick={() => setIsOpen((prev) => !prev)} isOpen={isOpen} />
          {isOpen && (
            <>
              <div className='tw:flex tw:justify-end tw:gap-2 tw:mb-4'>
                <Button
                  variant='default'
                  onClick={() => setModalState({ isOpen: true, editIndex: null, activity: null, label: '', lockedFields: new Set() })}
                >
                  <Plus className='tw:size-4' />
                  Add Activity
                </Button>
                {uploadSession && (
                  <Button
                    variant='outline'
                    onClick={() => setScanInvoiceModalOpen(true)}
                  >
                    <FileText className='tw:size-4' />
                    Upload Invoice
                  </Button>
                )}
              </div>

              {activities.length === 0 ? (
                <div className='tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:bg-muted/20 tw:p-10 tw:text-center tw:mb-6'>
                  <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                    No activities yet. Click <strong>Add Activity</strong> to log fuel, energy or refrigerant use against this asset.
                  </p>
                </div>
              ) : (
                <StagingTable
                  rows={activities}
                  groupById={EMPTY_GROUP_MAP}
                  initiativeId={initiativeId}
                  onEdit={handleEditFromTable}
                  onDelete={handleDeleteFromTable}
                  onPickAlternative={handlePickAlternative}
                  onRevertOverride={handleRevertOverride}
                  onRetriggerFactorSearch={handleRetriggerFactorSearch}
                  showSourceColumn={false}
                />
              )}
            </>
          )}
        </div>

        <AddActivityModal
          isOpen={modalState.isOpen}
          categories={categories}
          defaults={{
            // Asset country wins (it's a stronger signal — user explicitly
            // tagged the asset's location); fall back to the org-level
            // default so manual-path assets without a country still
            // pre-populate the picker.
            region: staged.asset.region || defaultRegion,
            periodStart: '',
            periodEnd: '',
          }}
          extraEntryDefaults={extraEntryDefaults}
          extraLockedFields={extraLockedFields}
          editing={modalState.editIndex !== null && modalState.activity ? { entry: modalState.activity, label: modalState.label } : null}
          onSave={handleAddSave}
          onClose={() => setModalState({ isOpen: false, editIndex: null, activity: null, label: '', lockedFields: new Set() })}
        />

        {uploadSession && (
          <ScanInvoiceModal
            isOpen={scanInvoiceModalOpen}
            initiativeId={initiativeId}
            uploadSession={uploadSession}
            onAccept={handleScanInvoiceAccept}
            onClose={() => setScanInvoiceModalOpen(false)}
          />
        )}
      </CardContent>
    </Card>
  );
};

export const AssetActivitiesStep = ({
  stagedAssets,
  onStagedAssetsChange,
  initiativeId,
  defaultRegion = '',
  uploadSession,
}: AssetActivitiesStepProps) => {
  if (stagedAssets.length === 0) {
    return (
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-10 tw:text-center'>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            No assets staged. Go back and select an asset type to start.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Manual path: 1 asset, default-open. Bulk path: N assets, default-collapsed.
  const defaultOpen = stagedAssets.length === 1;

  return (
    <>
      {stagedAssets.map((staged, i) => (
        <AssetBlock
          key={staged.id}
          staged={staged}
          index={i}
          onStagedAssetsChange={onStagedAssetsChange}
          initiativeId={initiativeId}
          defaultOpen={defaultOpen}
          defaultRegion={defaultRegion}
          uploadSession={uploadSession}
        />
      ))}
    </>
  );
};
